import {UtilService} from './../../../../shared/utils/util.service';
import {APP_BASE_HREF} from '@angular/common';
import {Inject, Injectable, Optional} from '@angular/core';
import {CookieService} from 'ngx-cookie-service';
import {catchError} from 'rxjs/operators';
import {CommonServiceService} from 'src/app/shared/service/common-service.service';
import {HttpUtil} from 'src/app/shared/service/common.http';
import {GlobalCacheService} from 'src/app/shared/service/global-cache.service';
import {HandleError, HttpErrorHandler} from 'src/app/shared/service/http-error-handler.service';

const disposePropertyKeys = [
  {label: '规格', value: 'specification'},
  {label: '尺寸', value: 'size'},
  {label: '材质', value: 'material'},
  {label: '单位', value: 'unit'},
  {label: '包装', value: 'packing'},
  {label: '包装语言', value: 'packagingLanguage'},
  {label: '中包数量', value: 'packageRequirements'},
];

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  constructor(
    private httpUtil: HttpUtil,
    private httpErrorHandler: HttpErrorHandler,
    private cookieService: CookieService,
    private globalCacheService: GlobalCacheService,
    public commonService: CommonServiceService,
    private utilService: UtilService,
    @Optional()
    @Inject(APP_BASE_HREF)
      origin: string
  ) {
    this.handleError = httpErrorHandler.createHandleError('WeeklyService');
    this.host = `${origin}`;
  }

  private handleError: HandleError;
  private host = '';
  public api = {
    property: () => `${this.host}/a/classify/property/search`,
    search: () => `${this.host}/a/sku/search`,
    userSearch: () => `${this.host}/a/u/sku/search`,
    detail: (id) => `${this.host}/a/spu/${id}`,
    sku: (id) => `${this.host}/a/u/sku/${id}`,
    brand: () => `${this.host}/a/brand/search`,
  };

  // 品类列表
  getPropertyList(params): Promise<any> {
    const lang = this.commonService.getLocalStorageByKey('language') || '';
    const merchantId = this.commonService.getLocalStorageByKey('merchantId');
    params.merchantId = merchantId;
    params.lang = lang;
    return this.httpUtil
      .get(this.api.property(), params)
      .pipe(catchError(this.handleError('getPropertyList', [])))
      .toPromise()
      .then((res: any) => (res.code === 0 ? res : Promise.reject(res)));
  }

  // sku列表
  getSkuList(params): Promise<any> {
    const lang = this.commonService.getLocalStorageByKey('language') || '';
    const merchantId = this.commonService.getLocalStorageByKey('merchantId');
    const userInfo = this.commonService.getLocalStorageByKey('userInfo');
    params.merchantId = merchantId;
    params.lang = lang;
    params.status = 1;
    return this.httpUtil
      .get(userInfo && userInfo.id ? this.api.userSearch() : this.api.search(), params)
      .pipe(catchError(this.handleError('getSkuList', [])))
      .toPromise()
      .then((res: any) => (res.code === 0 ? res : Promise.reject(res)));
  }

  // spu详情
  getSpuDetail(id): Promise<any> {
    const lang = this.commonService.getLocalStorageByKey('language') || '';
    const params = {
      lang,
      merchantId: this.commonService.getLocalStorageByKey('merchantId'),
    };
    return this.httpUtil
      .get(this.api.detail(id), params)
      .pipe(catchError(this.handleError('getSpuDetail', [])))
      .toPromise()
      .then((res: any) => (res.code === 0 ? res : Promise.reject(res)));
  }

  // spu详情
  getSkuDetail(id): Promise<any> {
    const lang = this.commonService.getLocalStorageByKey('language') || '';
    const merchantId = this.commonService.getLocalStorageByKey('merchantId');
    const params = {
      lang,
      merchantId
    };
    return this.httpUtil
      .get(this.api.sku(id), params)
      .pipe(catchError(this.handleError('getSkuDetail', [])))
      .toPromise()
      .then((res: any) => (res.code === 0 ? res : Promise.reject(res)));
  }

  // 品牌列表
  getBrandList(params): Promise<any> {
    const lang = this.commonService.getLocalStorageByKey('language') || '';
    const merchantId = this.commonService.getLocalStorageByKey('merchantId');
    params.merchantId = merchantId;
    params.lang = lang;
    return this.httpUtil
      .get(this.api.brand(), params)
      .pipe(catchError(this.handleError('getBrandList', [])))
      .toPromise()
      .then((res: any) => (res.code === 0 ? res : Promise.reject(res)));
  }

  disposeProductListData(data = {}, lang = 'Cn') {
    const {skuList = [], spuMap = {}} = data as any;
    return skuList.map(sku => {
      const {proItemSet = '', spuId = ''} = sku;
      const spu = spuMap[spuId] || {};
      const cloneSpu = JSON.parse(JSON.stringify(spu));
      cloneSpu.titleLang = this.utilService.getJSONParse(cloneSpu.titleLang, {});
      cloneSpu.sloganLang = this.utilService.getJSONParse(cloneSpu.sloganLang, {});
      sku.spu = cloneSpu;
      const [firstClassify = '/', secondClassify = '/', thirdClassify = '/'] = cloneSpu.classifyDesc.split('/');
      const extend = this.disposeSpuProperty(proItemSet, cloneSpu, lang);

      return {
        ...sku,
        firstClassify, secondClassify, thirdClassify,
        ...extend
      };
    });
  }

  disposeSpuProperty(skuProItemSet = '', spu = {}, lang = 'Cn') {
    const {
      // keyword1 = '', // 自定义规格(propertyItemList里后端已返回，所以这里用不到了）
      proItemSet = '', // 当前sku规格
      propertyItemList = [] // spu所有规格
    } = spu as any;
    const skuProItemList = skuProItemSet.split(',').filter(id => !!id); // sku选中的规格
    const spuProItemList = proItemSet.split(',').filter(id => !!id); // spu全部选中的规格
    // 筛选选中的规格和参数
    const selectedItems = this.getSelectedItems(propertyItemList, spuProItemList, skuProItemList);
    // 将筛选的规格和参数父级和子级转换 然后合并到一起
    const checkedProItemMap = this.organizeSelectedItems(selectedItems);
    // 通过循环配置的keys来设置对应展示的字段
    const res = this.mapPropertyKeysToValues(checkedProItemMap, lang);
    return res;
  }

  // 筛选选中的规格和参数
  getSelectedItems(propertyItemList, spuProItemList, skuProItemList = []) {
    return propertyItemList.filter(item => {
      return (skuProItemList.length && item.property.type === 1) ? skuProItemList.includes(`${item.idStr}`) : spuProItemList.includes(`${item.idStr}`);
    });
  }

  // 将筛选的规格和参数父级和子级转换 然后合并到一起
  organizeSelectedItems(selectedItems) {
    const checkedProItemMap = {};
    selectedItems.forEach(item => {
      if (!checkedProItemMap[item.propertyId]) {
        checkedProItemMap[item.propertyId] = {...item.property};
        checkedProItemMap[item.propertyId].itemList = [];
      }
      checkedProItemMap[item.propertyId].itemList.push(item);
    });
    return checkedProItemMap;
  }

  // 通过循环配置的keys来设置对应展示的字段
  mapPropertyKeysToValues(checkedProItemMap, lang) {
    const res = {};
    disposePropertyKeys.forEach(({label, value: key}) => {
      const item = (Object.entries(checkedProItemMap) as any).find(([k, v]) => {
        if (v.type === 1) { // type 1 为规格 直接对比
          return v.title === label;
        } else if (v.type === 2) { // type 2 为参数 需要对比@后面的参数
          const [temp, title = ''] = v.title.split('@');
          return title === label;
        }
      });
      if (item) {
        res[key] = item[1].itemList.map(i => {
          const titleLang = this.utilService.getJSONParse(i.titleLang, {});
          return titleLang[lang] || i.title;
        }).join(',');
      }
    });
    return res;
  }
}
